import { type ReactNode } from 'react';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, createPhotoInsurance } from '~shared/actions/insurances';
import { updateCashPay } from '~shared/actions/users';
import getErrors from '~shared/features/ui/selectors/getErrors';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getSelectedUserID } from '~shared/features/ui/selectors/getUsers';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getInsuranceSource from '~shared/lib/analytics/src/getInsuranceSource';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { connect } from '~shared/store';
import { type ReduxDispatchShared, type ReduxStateShared } from '~shared/types';
import InsurancePhotoForm from './InsurancePhotoForm';
import { type Values } from './InsurancePhotoFormElements';

const mapStateToProps = (state: ReduxStateShared) => {
  const errors = getErrors(state);
  const loading = getLoading(state);
  const selectedUserID = getSelectedUserID(state);
  const currentUserID = getCurrentUserID(state);
  const source = getInsuranceSource(state);

  const insuranceError =
    errors.fetchInsurancesError ||
    errors.createInsuranceError ||
    errors.updateInsuranceError ||
    errors.createPhotoInsuranceError;

  return {
    insuranceError,
    loading: loading.createPhotoInsuranceLoading,
    source,
    userID: selectedUserID || currentUserID,
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatchShared) => ({
  clearInsuranceError: (...args: Parameters<typeof clearInsuranceError>) => dispatch(clearInsuranceError(...args)),
  // eslint-disable-next-line import/no-deprecated
  createPhotoInsurance: (...args: Parameters<typeof createPhotoInsurance>) => dispatch(createPhotoInsurance(...args)),
  sendAnalyticsEvent: (...args: Parameters<typeof sendAnalyticsEvent>) => {
    dispatch(sendAnalyticsEvent(...args));
  },
  updateCashPay: (...args: Parameters<typeof updateCashPay>) => dispatch(updateCashPay(...args)),
});

const mergeProps = (
  stateProps: ReturnType<typeof mapStateToProps>,
  dispatchProps: ReturnType<typeof mapDispatchToProps>,
  ownProps: {
    onCancel: () => void;
    customHeader?: ReactNode;
  },
) => {
  const { source, userID, ...newStateProps } = stateProps;
  const { clearInsuranceError, createPhotoInsurance, sendAnalyticsEvent, updateCashPay } = dispatchProps;

  return {
    ...newStateProps,
    ...ownProps,
    onSubmit: async (insurance: Values) => {
      clearInsuranceError();
      const images = [
        insurance.image_url?.length && insurance.image_url[0],
        insurance.image_url_2?.length && insurance.image_url_2[0],
      ];
      const filteredImages = images.filter(Boolean);

      if (filteredImages.length) {
        // @ts-expect-error I'm pretty convinced we're actually using createPhotoInsurance wrong here but it's out-of-scope
        // of this PR to fix it.
        const insurance = await createPhotoInsurance(filteredImages, userID);
        if (typeof insurance !== 'boolean') {
          sendAnalyticsEvent(
            createEvent(EVENTS.INSURANCE_ADDED, {
              type: 'photo',
              source,
              entity_name: insurance.insurance_plan_name ?? '',
            }),
          );
        }

        if (userID) {
          const { success } = await updateCashPay(userID, false);
          if (success) {
            sendAnalyticsEvent(
              createEvent(EVENTS.CASH_PAY_ONLY_SET, {
                source,
                'new value': false,
              }),
            );
          }
        }
      }
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(InsurancePhotoForm);
